import { computed } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';

import {
  CommissionInvoiceListFilters,
  cloneInvoiceListFilters,
  defaultInvoiceListFilters,
} from '../../../../types/commission-invoice.types';
import { OrganizationDTO } from '../../../../types/organization.types';

export interface CommissionInvoiceFilterDisplayPill {
  key: string;
  label: string;
  type: 'status' | 'payeeType' | 'payeeOrganization' | 'payerOrganization';
}

interface CommissionInvoiceFilterDisplayState {
  isDialogOpen: boolean;
  draftFilters: CommissionInvoiceListFilters;
  appliedFilters: CommissionInvoiceListFilters;
}

const invoiceStatusLabels: Record<string, string> = {
  all: 'All statuses',
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sent: 'Sent',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

const payeeTypeLabels: Record<string, string> = {
  all: 'All payees',
  expert_individual: 'Expert Individual',
  expert_organization: 'Expert Organization',
};

const initialState: CommissionInvoiceFilterDisplayState = {
  isDialogOpen: false,
  draftFilters: defaultInvoiceListFilters(),
  appliedFilters: defaultInvoiceListFilters(),
};

export const CommissionInvoiceFilterDisplayStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasActiveFilters: computed(() => {
      const filters = store.appliedFilters();
      return (
        filters.status !== 'all' ||
        filters.payeeType !== 'all' ||
        !!filters.payeeOrganization ||
        !!filters.payerOrganization
      );
    }),
    activeFilterPills: computed<CommissionInvoiceFilterDisplayPill[]>(() => {
      const filters = store.appliedFilters();
      return [
        ...(filters.status !== 'all'
          ? [
              {
                key: 'status',
                label: `Status: ${
                  invoiceStatusLabels[filters.status] ?? filters.status
                }`,
                type: 'status' as const,
              },
            ]
          : []),
        ...(filters.payeeType !== 'all'
          ? [
              {
                key: 'payeeType',
                label: `Payee: ${
                  payeeTypeLabels[filters.payeeType] ?? filters.payeeType
                }`,
                type: 'payeeType' as const,
              },
            ]
          : []),
        ...(filters.payeeOrganization
          ? [
              {
                key: 'payeeOrganization',
                label: `Payee Org: ${
                  filters.payeeOrganization.name ?? filters.payeeOrganization.id
                }`,
                type: 'payeeOrganization' as const,
              },
            ]
          : []),
        ...(filters.payerOrganization
          ? [
              {
                key: 'payerOrganization',
                label: `Payer Org: ${
                  filters.payerOrganization.name ?? filters.payerOrganization.id
                }`,
                type: 'payerOrganization' as const,
              },
            ]
          : []),
      ];
    }),
  })),
  withMethods((store) => ({
    openDialog(appliedFilters: CommissionInvoiceListFilters): void {
      const nextFilters = cloneInvoiceListFilters(appliedFilters);
      patchState(store, {
        isDialogOpen: true,
        appliedFilters: nextFilters,
        draftFilters: cloneInvoiceListFilters(nextFilters),
      });
    },

    closeDialog(): void {
      patchState(store, {
        isDialogOpen: false,
        draftFilters: cloneInvoiceListFilters(store.appliedFilters()),
      });
    },

    resetDraft(): void {
      patchState(store, { draftFilters: defaultInvoiceListFilters() });
    },

    syncAppliedFilters(filters: CommissionInvoiceListFilters): void {
      const nextFilters = cloneInvoiceListFilters(filters);
      patchState(store, {
        appliedFilters: nextFilters,
        draftFilters: cloneInvoiceListFilters(nextFilters),
      });
    },

    setStatus(value: CommissionInvoiceListFilters['status']): void {
      patchState(store, {
        draftFilters: { ...store.draftFilters(), status: value ?? 'all' },
      });
    },

    setPayeeType(value: CommissionInvoiceListFilters['payeeType']): void {
      patchState(store, {
        draftFilters: { ...store.draftFilters(), payeeType: value ?? 'all' },
      });
    },

    setPayeeOrganization(organization: OrganizationDTO | null): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          payeeOrganization: organization,
        },
      });
    },

    setPayerOrganization(organization: OrganizationDTO | null): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          payerOrganization: organization,
        },
      });
    },

    applyDraft(): CommissionInvoiceListFilters {
      const nextFilters = cloneInvoiceListFilters(store.draftFilters());
      patchState(store, {
        isDialogOpen: false,
        appliedFilters: nextFilters,
        draftFilters: cloneInvoiceListFilters(nextFilters),
      });
      return nextFilters;
    },

    clearAppliedFilter(
      pill: CommissionInvoiceFilterDisplayPill
    ): CommissionInvoiceListFilters {
      const filters = cloneInvoiceListFilters(store.appliedFilters());
      if (pill.type === 'status') filters.status = 'all';
      if (pill.type === 'payeeType') filters.payeeType = 'all';
      if (pill.type === 'payeeOrganization') filters.payeeOrganization = null;
      if (pill.type === 'payerOrganization') filters.payerOrganization = null;
      this.syncAppliedFilters(filters);
      return filters;
    },

    clearAllAppliedFilters(): CommissionInvoiceListFilters {
      const filters = defaultInvoiceListFilters();
      this.syncAppliedFilters(filters);
      return filters;
    },
  }))
);
